import React from 'react';
import { Button } from '../ui/button';
import { Share2, Facebook, Instagram, LinkedinIcon, Send } from 'lucide-react';
import { WhatsAppIcon } from '../icons/WhatsAppIcon';
import { TikTokIcon } from '../icons/TikTokIcon';
import { XTwitterIcon } from '../icons/XIcon';

interface SocialShareProps {
  className?: string;
  title?: string;
  description?: string;
  showMainButton?: boolean;
  hideMainButton?: 'mobile' | 'always' | 'never';
}

export const SocialShare: React.FC<SocialShareProps> = ({
  className,
  title = "Partager",
  description = "Découvrez cette page !",
  showMainButton = true,
  hideMainButton = 'never'
}) => {
  const getCurrentUrl = () => {
    return window.location.href;
  };

  const handleShare = async () => {
    const url = getCurrentUrl();
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url: url,
        });
      } catch (error) {
        console.log('Erreur lors du partage:', error);
      }
    }
  };

  const shareOnFacebook = () => {
    const url = getCurrentUrl();
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = () => {
    const url = getCurrentUrl();
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(description)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareOnWhatsApp = () => {
    const url = getCurrentUrl();
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(description + ' ' + url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareOnLinkedIn = () => {
    const url = getCurrentUrl();
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const shareOnTelegram = () => {
    const url = getCurrentUrl();
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(description)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const copyToClipboard = async () => {
    const url = getCurrentUrl();
    try {
      await navigator.clipboard.writeText(url);
      alert('Lien copié dans le presse-papiers !');
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
    }
  };

  const socialButtons = [
    {
      icon: Facebook,
      onClick: shareOnFacebook,
      title: "Partager sur Facebook",
      className: "bg-[#1877F2]/20 border-[#1877F2]/30 hover:bg-[#1877F2]/30"
    },
    {
      icon: Instagram,
      onClick: copyToClipboard,
      title: "Copier le lien pour Instagram",
      className: "bg-gradient-to-br from-[#833AB4]/20 via-[#FD1D1D]/20 to-[#F77737]/20 border-[#833AB4]/30 hover:from-[#833AB4]/30 hover:via-[#FD1D1D]/30 hover:to-[#F77737]/30"
    },
    {
      icon: WhatsAppIcon,
      onClick: shareOnWhatsApp,
      title: "Partager sur WhatsApp",
      className: "bg-[#25D366]/20 border-[#25D366]/30 hover:bg-[#25D366]/30"
    },
    {
      icon: XTwitterIcon,
      onClick: shareOnTwitter,
      title: "Partager sur X",
      className: "bg-[#1DA1F2]/20 border-[#1DA1F2]/30 hover:bg-[#1DA1F2]/30"
    },
    {
      icon: TikTokIcon,
      onClick: copyToClipboard,
      title: "Copier le lien pour TikTok",
      className: "bg-gradient-to-r from-[#FF0050]/20 via-[#00F2EA]/20 to-[#FF0050]/20 border-[#FF0050]/30 hover:from-[#FF0050]/30 hover:via-[#00F2EA]/30 hover:to-[#FF0050]/30"
    },
    {
      icon: LinkedinIcon,
      onClick: shareOnLinkedIn,
      title: "Partager sur LinkedIn",
      className: "bg-[#0A66C2]/20 border-[#0A66C2]/30 hover:bg-[#0A66C2]/30"
    },
    {
      icon: Send,
      onClick: shareOnTelegram,
      title: "Partager sur Telegram",
      className: "bg-[#0088cc]/20 border-[#0088cc]/30 hover:bg-[#0088cc]/30"
    }
  ];

  return (
    <div className={`flex items-center justify-start gap-4 ${className}`}>
      <div className="flex items-center gap-3">
        {showMainButton && hideMainButton !== 'always' && (
          <Button
            variant="outline"
            className={`bg-white/20 text-black border-white/30 hover:bg-white/30 backdrop-blur-sm ${
              hideMainButton === 'mobile' ? 'hidden md:flex' : 'flex'
            }`}
            onClick={handleShare}
          >
            <Share2 className="w-5 h-5 mr-2" />
            {title}
          </Button>
        )}

        <div className="flex gap-2">
          {socialButtons.map((button, index) => {
            const Icon = button.icon;
            return (
              <Button
                key={index}
                variant="outline"
                size="icon"
                className={`text-black backdrop-blur-sm ${button.className}`}
                onClick={button.onClick}
                title={button.title}
              >
                <Icon className="w-5 h-5" />
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
